import React from "react";
import {
  CardContent,
  Typography,
  Box,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/styles";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteIcon from "@material-ui/icons/Delete";
import DescriptionIcon from "@material-ui/icons/Description";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import ImageIcon from "@material-ui/icons/Image";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { capitalizarPalabras } from "src/utils/sharedFunctions";
import { useFileUploadTarea } from "./hooks/useFileUploadTarea";

const useStyles = makeStyles((theme) => ({
  expandedContent: {
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  descripcionFull: {
    backgroundColor: "white",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  uploadZone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor: "white",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}05`,
    },
  },
  uploadZoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}10`,
  },
  uploadIcon: {
    fontSize: 48,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  hidden: {
    display: "none",
  },
  fileList: {
    backgroundColor: "white",
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  fileItem: {
    padding: theme.spacing(1.5, 2),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  fileIcon: {
    minWidth: 40,
  },
  selectedFileInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info.lighter,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  uploadButton: {
    marginTop: theme.spacing(2),
  },
  emptyFileState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

// Función para obtener el icono según el tipo de archivo
const getFileIcon = (fileName) => {
  const extension = fileName?.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "pdf":
      return <PictureAsPdfIcon style={{ color: "#f44336" }} />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "svg":
      return <ImageIcon style={{ color: "#4caf50" }} />;
    case "doc":
    case "docx":
      return <DescriptionIcon style={{ color: "#2196f3" }} />;
    default:
      return <InsertDriveFileIcon style={{ color: "#757575" }} />;
  }
};

export default function PortafolioTareaCardContent({
  portafolio,
  tarea,
  esFechaHoy,
  onDeleteFile,
  deletingFile,
}) {
  const classes = useStyles();
  const theme = useTheme();

  const {
    selectedFile,
    uploading,
    uploadError,
    handleFileSelect,
    clearSelectedFile,
    uploadFile,
  } = useFileUploadTarea(tarea.id);

  const handleUpload = async () => {
    await uploadFile(portafolio.id);
  };

  const handleDeleteFile = async (archivo) => {
    await onDeleteFile(archivo);
  };

  const archivoCount = portafolio.archivosPortafolio?.length || 0;

  return (
    <CardContent className={classes.expandedContent}>
      {/* Descripción completa */}
      <div className={classes.section}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          <DescriptionIcon fontSize="small" />
          Descripción Completa
        </Typography>
        <div className={classes.descripcionFull}>
          <Typography variant="body2">{portafolio.descripcion}</Typography>
        </div>
      </div>

      {/* Sección de archivos */}
      <div className={classes.section}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          <AttachFileIcon fontSize="small" />
          Archivos Adjuntos ({archivoCount})
        </Typography>

        {/* Upload de archivos (solo para fecha actual) */}
        {esFechaHoy && (
          <Box marginBottom={2}>
            <input
              className={classes.hidden}
              id={`file-input-${portafolio.id}`}
              type="file"
              onChange={handleFileSelect}
              accept="*/*"
            />

            <label htmlFor={`file-input-${portafolio.id}`}>
              <div
                className={`${classes.uploadZone} ${
                  selectedFile ? classes.uploadZoneActive : ""
                }`}
              >
                <CloudUploadIcon className={classes.uploadIcon} />
                <Typography variant="body2" gutterBottom>
                  {selectedFile
                    ? "Archivo seleccionado"
                    : "Subir nuevo archivo"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedFile
                    ? selectedFile.name
                    : "Haz clic o arrastra un archivo aquí (máx. 15MB)"}
                </Typography>
              </div>
            </label>

            {selectedFile && (
              <Box className={classes.selectedFileInfo}>
                <Box display="flex" alignItems="center" gap={1}>
                  {getFileIcon(selectedFile.name)}
                  <Box>
                    <Typography variant="body2">{selectedFile.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    onClick={clearSelectedFile}
                    disabled={uploading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleUpload}
                    disabled={uploading}
                    startIcon={
                      uploading ? <CircularProgress size={16} /> : null
                    }
                  >
                    {uploading ? "Subiendo..." : "Subir"}
                  </Button>
                </Box>
              </Box>
            )}

            {uploadError && (
              <Typography
                variant="caption"
                color="error"
                style={{ display: "block", marginTop: 8 }}
              >
                {uploadError}
              </Typography>
            )}
          </Box>
        )}

        {/* Lista de archivos */}
        {archivoCount > 0 ? (
          <Paper className={classes.fileList}>
            <List dense>
              {portafolio.archivosPortafolio.map((archivo, index) => (
                <React.Fragment key={`archivo_${archivo.id || index}`}>
                  <ListItem className={classes.fileItem}>
                    <ListItemIcon className={classes.fileIcon}>
                      {getFileIcon(archivo.nombreArchivo)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Button
                          onClick={() =>
                            window.open(archivo.urlArchivo, "_blank")
                          }
                          style={{
                            textTransform: "none",
                            justifyContent: "flex-start",
                            padding: 0,
                            minWidth: "auto",
                            color: theme.palette.primary.main,
                            fontWeight: 400,
                          }}
                        >
                          {archivo.nombreArchivo}
                        </Button>
                      }
                      secondary={`Subido por ${
                        capitalizarPalabras(
                          portafolio.ultimaModificacion?.empleado,
                        ) || "Usuario"
                      }`}
                    />
                    {esFechaHoy && (
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteFile(archivo)}
                          color="secondary"
                          size="small"
                          disabled={deletingFile}
                        >
                          {!deletingFile ? (
                            <DeleteIcon fontSize="small" />
                          ) : (
                            <CircularProgress size={20} />
                          )}
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                  {index < portafolio.archivosPortafolio.length - 1 && (
                    <Divider />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        ) : (
          <div className={classes.emptyFileState}>
            <AttachFileIcon
              style={{ fontSize: 32, color: "#ccc", marginBottom: 8 }}
            />
            <Typography variant="body2" gutterBottom>
              No hay archivos adjuntos
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {esFechaHoy
                ? "Sube archivos usando la zona de arriba"
                : "Los archivos se pueden subir solo el día de creación"}
            </Typography>
          </div>
        )}
      </div>
    </CardContent>
  );
}
